import { Component, computed, effect, inject, input } from '@angular/core';
import { ThemedIconStore } from './themed-icon.store';
import type { SizeValue } from './themed-icon.types';
import { OcpImageComponent } from '../image/image.component';

@Component({
  selector: 'ocp-themed-icon',
  host: {
    class: 'ocp-themed-icon-host',
  },
  imports: [OcpImageComponent],
  template: `
    <ocp-image
      [src]="store.resolvedSrc()"
      [imgClass]="imageClass()"
      [optimized]="true"
      [width]="store.resolvedWidthPx()"
      [height]="store.resolvedHeightPx()"
      [style.width]="store.resolvedWidthCss()"
      [style.height]="store.resolvedHeightCss()"
      [role]="store.ariaLabel() ? 'img' : ''"
      [ariaLabel]="store.ariaLabel()"
      [ariaHidden]="store.ariaLabel() ? null : 'true'"
      [alt]="store.alt() || store.ariaLabel()"
    />
  `,
  styleUrl: './themed-icon.component.scss',
  providers: [ThemedIconStore],
})
export class ThemedIconComponent {
  protected readonly store = inject(ThemedIconStore);
  protected readonly imageClass = computed(() =>
    ['themed-icon', this.class()].filter(Boolean).join(' ')
  );

  /** Icon filename, e.g. "models.svg" */
  readonly name = input.required<string>();

  /** Icon class name */
  readonly class = input<string>('');

  /** Width and height. Accepts a pixel number (`24`), a px string (`'24px'`), or a rem string (`'1.5rem'`). */
  readonly size = input<SizeValue>(18);

  /** Override width independently. Falls back to `size`. */
  readonly width = input<SizeValue | undefined>(undefined);

  /** Override height independently. Falls back to `size`. */
  readonly height = input<SizeValue | undefined>(undefined);

  /** Accessible label. If omitted, icon is decorative (aria-hidden). */
  readonly ariaLabel = input<string>('');

  /** Alt text for the underlying img element. Defaults to empty string (decorative). */
  readonly alt = input<string>('');

  /**
   * Controls which icon variant is used.
   * - `'light'` — always use the light variant
   * - `'dark'`  — always use the dark variant
   * - `'swap'`  — invert the active theme (dark icon in light mode, light icon in dark mode)
   * - `undefined` (default) — follow the active theme
   */
  readonly mode = input<'light' | 'dark' | 'swap' | undefined>(undefined);

  constructor() {
    effect(() => this.store.name.set(this.name()));
    effect(() => this.store.size.set(this.size()));
    effect(() => this.store.width.set(this.width()));
    effect(() => this.store.height.set(this.height()));
    effect(() => this.store.ariaLabel.set(this.ariaLabel()));
    effect(() => this.store.alt.set(this.alt()));
    effect(() => this.store.mode.set(this.mode()));
  }
}
